import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarX2, CheckCircle2, Loader2, RefreshCw, XCircle, type LucideIcon } from 'lucide-react';
import { WITHDRAWAL_TOKEN, confirmLink, statusOf, viewLink, withdrawalErrorText } from '../api/withdrawals';
import { Button, Callout, Card, cx } from '../components/charte';
import { WithdrawalAck, WithdrawalForm, subjectWords } from '../components/WithdrawalForm';
import { fmtDayFull } from '../lib/dates';
import type { DeclaredWithdrawal, PublicWithdrawal, WithdrawalAck as WithdrawalAckData } from '../types';

// Page publique « Renoncer au contrat ici » (24/10/2026, vente à distance, T17) : ouverte par le lien de l'e-mail de
// fin de signature, sans compte. Même cadre que la page de signature : jeton lu dans le fragment puis retiré de
// l'adresse, aucun référent transmis. Hors du délai ouvert, le serveur ne rend ni nom ni adresse ; au-delà de la date
// limite + 31 jours, le lien est invalide (404).

const TONES = {
  green: 'bg-success-light text-success-dark',
  red: 'bg-danger-light text-danger-dark',
  mute: 'bg-surface-2 text-secondary',
};

/** Bloc d'état : pastille d'icône et phrase (comme la page de signature). */
function StateCard({ icon: Icon, tone, children }: { icon: LucideIcon; tone: keyof typeof TONES; children: ReactNode }) {
  return (
    <Card className="flex items-start gap-4">
      <span className={cx('grid h-11 w-11 shrink-0 place-items-center rounded-2xl', TONES[tone])}>
        <Icon size={20} aria-hidden />
      </span>
      <p className="min-w-0 pt-2.5 text-[15px] leading-relaxed text-body">{children}</p>
    </Card>
  );
}

/** Élève titulaire du lien, une fois sa rétractation enregistrée : son espace (jamais pour le représentant légal). */
const toPortal = (
  <a className="inline-block text-sm font-medium text-primary-600 underline-offset-[3px] hover:underline" href="/">
    Aller à mon espace élève
  </a>
);

const leadOf = (view: PublicWithdrawal, school: string) =>
  `${subjectWords(view.subject.kind).title} ${view.subject.number} avec ${school}, conclu à distance le ${fmtDayFull(view.concludedOn)}.`;

export default function WithdrawalPage() {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '');
  const wellFormed = WITHDRAWAL_TOKEN.test(token);
  useEffect(() => {
    window.history.replaceState({}, '', '/renoncer');
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
  const link = useQuery({
    queryKey: ['withdrawal-link'],
    queryFn: () => viewLink(token),
    enabled: wellFormed,
    retry: false,
  });
  const [ack, setAck] = useState<WithdrawalAckData | null>(null);
  const [notice, setNotice] = useState('');

  async function confirm(declared: DeclaredWithdrawal): Promise<WithdrawalAckData> {
    setNotice('');
    try {
      const result = await confirmLink(token, declared);
      if (!result.ack) throw new Error('Opération indisponible. Réessayez.');
      setAck(result.ack);
      return result.ack;
    } catch (error) {
      // Délai passé entre-temps : message du serveur, puis relecture du lien (la page dit alors que le délai est fini).
      if (statusOf(error) === 409) {
        setNotice(withdrawalErrorText(error));
        await link.refetch();
      }
      throw error;
    }
  }

  const view = link.data;
  const words = subjectWords(view?.subject.kind ?? 'contract');
  const school = view?.school || 'votre auto-école';
  let title = 'Renoncer à votre contrat';
  let lead: string | null = null;
  let body: ReactNode;
  if (ack && view) {
    lead = leadOf(view, school);
    body = (
      <Card className="space-y-5">
        <WithdrawalAck ack={ack} />
        {view.signerRole === 'student' && toPortal}
      </Card>
    );
  } else if (!wellFormed || statusOf(link.error) === 404) {
    title = 'Lien de rétractation invalide';
    body = (
      <StateCard icon={XCircle} tone="red">
        Ce lien ne fonctionne pas. Utilisez le lien du dernier e-mail reçu, ou contactez votre auto-école.
      </StateCard>
    );
  } else if (!view) {
    // Le jeton n'est plus dans l'adresse : recharger la page ne le retrouverait pas, d'où « Réessayer ».
    body = link.isError ? (
      <Card className="space-y-4">
        <div role="alert">
          <Callout tone="warn">{withdrawalErrorText(link.error)}</Callout>
        </div>
        <Button icon={RefreshCw} loading={link.isFetching} onClick={() => void link.refetch()}>
          Réessayer
        </Button>
      </Card>
    ) : (
      <Card className="flex items-center gap-2 text-sm text-secondary">
        <Loader2 size={16} className="animate-spin" aria-hidden />
        Chargement…
      </Card>
    );
  } else if (view.state === 'open' && view.defaults) {
    lead = leadOf(view, school);
    body = (
      <Card>
        <WithdrawalForm
          kind={view.subject.kind}
          number={view.subject.number}
          until={view.until}
          defaults={view.defaults}
          start="intro"
          onConfirm={confirm}
        />
      </Card>
    );
  } else if (view.state === 'withdrawn' && view.withdrawal) {
    const received = view.withdrawal;
    title = 'Rétractation déjà enregistrée';
    body = (
      <StateCard icon={CheckCircle2} tone="green">
        {`La rétractation de ${words.ce} a été reçue le ${fmtDayFull(received.receivedOn)}${received.receivedTime ? ` à ${received.receivedTime}` : ''}. Remboursement au plus tard le ${fmtDayFull(received.refundBy)}.`}
      </StateCard>
    );
  } else {
    title = 'Délai de rétractation terminé';
    body = (
      <StateCard icon={CalendarX2} tone="mute">
        {`Le délai pour renoncer à ${words.ce} a pris fin le ${fmtDayFull(view.until)}. Pour toute question, contactez ${school}.`}
      </StateCard>
    );
  }

  return (
    <main className="min-h-screen bg-hero-indigo px-4 py-8 text-body max-sm:py-6">
      <div className="mx-auto max-w-3xl space-y-6">
        <img src="/logo-permio-eleve.png" alt="Permio" className="h-8 w-auto" />
        <header className="space-y-2">
          <p className="text-eyebrow uppercase text-primary-600">Permio · Rétractation</p>
          <h1 className="text-title-md text-heading sm:text-title-lg">{title}</h1>
          {lead && <p className="text-sm text-secondary">{lead}</p>}
        </header>
        {notice && (
          <div role="alert">
            <Callout tone="warn">{notice}</Callout>
          </div>
        )}
        {body}
      </div>
    </main>
  );
}
